import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import VoiceTypeCard from './VoiceTypeCard';
import AIAssignmentModal from './AIAssignmentModal';
import { getAuditionSongs } from '../../../services/auditionSongService';

const AuditionListSection = ({ user, onError, isLoading, setIsLoading }) => {
  const [auditionSongs, setAuditionSongs] = useState([]);
  const [expandedVoiceType, setExpandedVoiceType] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);

  // Voice types from the database enum
  const voiceTypes = [
    { id: 'bass', label: 'Bass', icon: 'Music' },
    { id: 'baritone', label: 'Baritone', icon: 'Music' },
    { id: 'tenor', label: 'Tenor', icon: 'Music' },
    { id: 'alto', label: 'Alto', icon: 'Music' },
    { id: 'mezzo_soprano', label: 'Mezzo-Soprano', icon: 'Music' },
    { id: 'soprano', label: 'Soprano', icon: 'Music' }
  ];

  // Audition categories from the database enum
  const auditionCategories = [
    { id: 'happy_role_songs', label: 'Happy Role Songs' },
    { id: 'sad_role_songs', label: 'Sad Role Songs' },
    { id: 'show_off_songs', label: 'Show Off Songs' }
  ];

  // Load audition songs data
  useEffect(() => {
    loadAuditionSongs();
  }, []);

  const loadAuditionSongs = async () => {
    try {
      setIsLoading?.(true);
      onError?.(null);

      const { data, error } = await getAuditionSongs();

      if (error) {
        onError?.('Failed to load audition songs. Please try again.');
        return;
      }

      setAuditionSongs(data || []);
    } catch (err) {
      onError?.('An unexpected error occurred while loading audition songs.');
    } finally {
      setIsLoading?.(false);
    }
  };

  const handleVoiceTypeToggle = (voiceTypeId) => {
    if (expandedVoiceType === voiceTypeId) {
      setExpandedVoiceType(null);
    } else {
      setExpandedVoiceType(voiceTypeId);
    }
  };

  const handleSongAssigned = async () => {
    // Refresh the audition songs data when a new song is assigned
    await loadAuditionSongs();
  };

  const handleSongRemoved = async () => {
    // Refresh the audition songs data when a song is removed
    await loadAuditionSongs();
  };

  const handleAIAssignmentsApplied = async () => {
    await loadAuditionSongs();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Loading skeleton */}
        {[...Array(6)]?.map((_, index) => (
          <div key={index} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-6 bg-white bg-opacity-20 rounded shimmer w-32"></div>
              <div className="h-4 bg-white bg-opacity-20 rounded shimmer w-16"></div>
            </div>
            <div className="space-y-3">
              {[...Array(3)]?.map((_, catIndex) => (
                <div key={catIndex} className="h-4 bg-white bg-opacity-20 rounded shimmer w-full"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
              <Icon name="Music" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-white">
                Audition Lists Management
              </h2>
              <p className="text-blue-100 text-sm font-body">
                Organize songs by voice type and audition category
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowAIModal(true)}
              iconName="Sparkles"
              iconPosition="left"
              className="border-violet-400 text-violet-300 hover:bg-violet-500 hover:text-white hover:border-violet-500 rounded-lg"
            >
              AI Auto-Assign
            </Button>
            <Button
              variant="outline"
              onClick={loadAuditionSongs}
              iconName="RefreshCw"
              iconPosition="left"
              disabled={isLoading}
              className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white rounded-lg"
            >
              Refresh Lists
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 bg-opacity-20 border border-blue-200 border-opacity-30 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-200 flex-shrink-0 mt-0.5" />
            <div className="text-blue-100 text-sm font-body">
              <p className="mb-2">
                <strong>How to manage audition lists:</strong>
              </p>
              <ul className="space-y-1 ml-4">
                <li>• Click on a voice type to expand and view audition categories</li>
                <li>• Each category can have up to 5 songs assigned</li>
                <li>• Search for songs and assign character roles for analysis</li>
                <li>• Use <strong>AI Auto-Assign</strong> to let OpenAI suggest voice types and categories for songs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {voiceTypes?.map((voiceType) => (
          <VoiceTypeCard
            key={voiceType?.id}
            voiceType={voiceType}
            auditionCategories={auditionCategories}
            auditionSongs={auditionSongs?.filter(song => song?.voice_type === voiceType?.id)}
            isExpanded={expandedVoiceType === voiceType?.id}
            onToggle={() => handleVoiceTypeToggle(voiceType?.id)}
            onSongAssigned={handleSongAssigned}
            onSongRemoved={handleSongRemoved}
            user={user}
            onError={onError}
          />
        ))}
      </div>

      {/* Statistics Summary */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-lg font-heading font-semibold text-white mb-4">
          Audition Lists Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {voiceTypes?.map((voiceType) => {
            const songCount = auditionSongs?.filter(song => song?.voice_type === voiceType?.id)?.length || 0;
            return (
              <div key={voiceType?.id} className="text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Icon name={voiceType?.icon} size={20} className="text-white" />
                </div>
                <div className="text-white font-medium text-sm mb-1">{voiceType?.label}</div>
                <div className="text-blue-200 text-xs font-body">{songCount} songs</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Assignment Modal */}
      {showAIModal && (
        <AIAssignmentModal
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          onAssignmentsApplied={handleAIAssignmentsApplied}
          user={user}
        />
      )}
    </div>
  );
};

export default AuditionListSection;